import { Box, Text } from '@chakra-ui/react'
import { FC, useEffect, useRef } from 'react'
import AsyncSelect from 'react-select/async'

import user from '@service/user'

export interface IUsuarioSapOption {
  value: string
  label: string
  nome?: string
  vinculadoA?: string
}

interface IUsuarioSapSelectProps {
  value: IUsuarioSapOption | null
  // Usuário SCL em edição: a opção vinculada a ele não fica desabilitada
  usuarioId?: number
  onChange: (option: IUsuarioSapOption | null) => void
}

// Administrador, Analista de Planejamento e Analista de Compras: os únicos
// perfis que o backend aceita com vínculo SAP
export const PERFIS_COM_USUARIO_SAP = [1, 12, 13]

export const podeVincularUsuarioSap = (idPerfil: number | string) =>
  PERFIS_COM_USUARIO_SAP.includes(Number(idPerfil))

const MIN_CHARS = 2
const DEBOUNCE_MS = 300

const UsuarioSapSelect: FC<IUsuarioSapSelectProps> = ({
  value,
  usuarioId,
  onChange
}) => {
  const timer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(timer.current!), [])

  const fetchOptions = async (busca: string) => {
    try {
      const { data: res } = await user.getUsuariosSap(busca)
      return (Array.isArray(res?.data) ? res.data : []).map(
        (item: any): IUsuarioSapOption => ({
          value: item.bname,
          nome: item.nome,
          label: `${item.bname} - ${item.nome ?? ''}`,
          vinculadoA:
            item.usuarioVinculadoId != null &&
            item.usuarioVinculadoId !== usuarioId
              ? item.usuarioVinculadoNome ?? 'outro usuário'
              : undefined
        })
      )
    } catch {
      // 400 (sem resultado / busca curta) vira lista vazia, sem toast
      return []
    }
  }

  // Debounce: só a última digitação dentro da janela chega a buscar
  const loadOptions = (inputValue: string) =>
    new Promise<IUsuarioSapOption[]>(resolve => {
      clearTimeout(timer.current!)
      const busca = inputValue.trim()
      if (busca.length < MIN_CHARS) {
        resolve([])
        return
      }
      timer.current = setTimeout(
        () => fetchOptions(busca).then(resolve),
        DEBOUNCE_MS
      )
    })

  return (
    <Box w="100%">
      <Text mb={2} fontSize="sm">
        Usuário SAP
      </Text>
      <AsyncSelect
        inputId="input-usuario-sap"
        classNamePrefix="react-select"
        placeholder="Digite o usuário ou nome SAP..."
        value={value}
        loadOptions={loadOptions}
        defaultOptions={false}
        isClearable
        isOptionDisabled={(option: IUsuarioSapOption) => !!option.vinculadoA}
        formatOptionLabel={(option: IUsuarioSapOption) =>
          option.vinculadoA
            ? `${option.label} (Vinculado a ${option.vinculadoA})`
            : option.label
        }
        noOptionsMessage={({ inputValue }) =>
          inputValue.trim().length < MIN_CHARS
            ? `Digite ao menos ${MIN_CHARS} caracteres`
            : 'Nenhum usuário SAP encontrado'
        }
        loadingMessage={() => 'Carregando...'}
        onChange={option => onChange((option as IUsuarioSapOption) ?? null)}
        menuPlacement="auto"
      />
    </Box>
  )
}

export default UsuarioSapSelect
